import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { MessageSquare, Reply, Trash2, Send, AtSign } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
});

function CommentSection({ projectId, targetType, targetId, targetName, user }) {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newComment, setNewComment] = useState("");
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchComments();
  }, [projectId, targetType, targetId]);

  const fetchComments = async () => {
    try {
      const response = await axios.get(
        `${API}/comments?project_id=${projectId}&target_type=${targetType}&target_id=${targetId}`,
        getAuthHeaders()
      );
      setComments(response.data.comments || []);
    } catch (error) {
      console.error("Failed to load comments:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;

    setSubmitting(true);
    try {
      await axios.post(
        `${API}/comments`,
        {
          project_id: projectId,
          target_type: targetType,
          target_id: targetId,
          content: newComment,
        },
        getAuthHeaders()
      );
      setNewComment("");
      fetchComments();
      toast.success("Comment added");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add comment");
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitReply = async (parentId) => {
    if (!replyText.trim()) return;

    setSubmitting(true);
    try {
      await axios.post(
        `${API}/comments`,
        {
          project_id: projectId,
          target_type: targetType,
          target_id: targetId,
          parent_id: parentId,
          content: replyText,
        },
        getAuthHeaders()
      );
      setReplyText("");
      setReplyingTo(null);
      fetchComments();
      toast.success("Reply added");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add reply");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (!confirm("Are you sure you want to delete this comment?")) return;

    try {
      await axios.delete(`${API}/comments/${commentId}`, getAuthHeaders());
      fetchComments();
      toast.success("Comment deleted");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete comment");
    }
  };

  const canDelete = (comment) => {
    return comment.author_email === user.email || user.role === "APPROVER";
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleString("en-IN", { timeZone: "Asia/Kolkata" });
  };

  const highlightMentions = (content) => {
    // Highlight @mentions
    return content.replace(
      /@([\w\.-]+@[\w\.-]+\.\w+)/g,
      '<span class="mention">@$1</span>'
    );
  };

  if (loading) {
    return (
      <div className="p-4 text-center text-gray-500" data-testid="comments-loading">
        Loading comments...
      </div>
    );
  }

  return (
    <div className="comment-section" data-testid="comment-section">
      <div className="flex items-center gap-2 mb-4">
        <MessageSquare className="w-5 h-5 text-gray-600" />
        <h3 className="font-semibold text-gray-800">
          Comments {targetName && `on ${targetName}`}
        </h3>
        <span className="text-sm text-gray-500">({comments.length})</span>
      </div>

      {/* New Comment Form */}
      <form onSubmit={handleSubmitComment} className="mb-6">
        <div className="relative">
          <Textarea
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            placeholder="Add a comment... (use @email to mention)"
            rows={2}
            className="pr-24"
            data-testid="new-comment-input"
          />
          <Button
            type="submit"
            size="sm"
            disabled={submitting || !newComment.trim()}
            className="absolute bottom-2 right-2"
            data-testid="submit-comment-btn"
          >
            <Send className="w-4 h-4 mr-1" />
            Post
          </Button>
        </div>
        <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
          <AtSign className="w-3 h-3" />
          Tip: Use @email@domain.com to mention someone
        </p>
      </form>

      {/* Comments List */}
      <div className="space-y-4">
        {comments.length === 0 ? (
          <p className="text-gray-500 text-sm text-center py-4" data-testid="no-comments">
            No comments yet. Be the first to comment!
          </p>
        ) : (
          comments.map((comment) => (
            <div
              key={comment.id}
              className="comment-item border rounded-lg p-4 bg-white"
              data-testid={`comment-${comment.id}`}
            >
              {/* Main Comment */}
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-medium text-gray-900">
                      {comment.author_email}
                    </span>
                    <span className="text-xs text-gray-500">
                      {formatDate(comment.created_at)}
                    </span>
                  </div>
                  <p
                    className="text-gray-700 whitespace-pre-wrap"
                    dangerouslySetInnerHTML={{
                      __html: highlightMentions(comment.content),
                    }}
                  />
                </div>
                <div className="flex gap-1 ml-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() =>
                      setReplyingTo(replyingTo === comment.id ? null : comment.id)
                    }
                    className="text-gray-500 hover:text-blue-600"
                    data-testid={`reply-btn-${comment.id}`}
                  >
                    <Reply className="w-4 h-4" />
                  </Button>
                  {canDelete(comment) && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteComment(comment.id)}
                      className="text-gray-500 hover:text-red-600"
                      data-testid={`delete-btn-${comment.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>

              {/* Reply Form */}
              {replyingTo === comment.id && (
                <div className="mt-3 pl-4 border-l-2 border-blue-200">
                  <Textarea
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    placeholder="Write a reply..."
                    rows={2}
                    className="mb-2"
                    data-testid={`reply-input-${comment.id}`}
                  />
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      onClick={() => handleSubmitReply(comment.id)}
                      disabled={submitting || !replyText.trim()}
                      data-testid={`submit-reply-${comment.id}`}
                    >
                      Reply
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        setReplyingTo(null);
                        setReplyText("");
                      }}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              )}

              {/* Replies */}
              {comment.replies && comment.replies.length > 0 && (
                <div className="mt-3 pl-4 border-l-2 border-gray-200 space-y-3">
                  {comment.replies.map((reply) => (
                    <div
                      key={reply.id}
                      className="bg-gray-50 p-3 rounded"
                      data-testid={`reply-${reply.id}`}
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-medium text-gray-900 text-sm">
                              {reply.author_email}
                            </span>
                            <span className="text-xs text-gray-500">
                              {formatDate(reply.created_at)}
                            </span>
                          </div>
                          <p
                            className="text-gray-700 text-sm whitespace-pre-wrap"
                            dangerouslySetInnerHTML={{
                              __html: highlightMentions(reply.content),
                            }}
                          />
                        </div>
                        {canDelete(reply) && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteComment(reply.id)}
                            className="text-gray-500 hover:text-red-600"
                            data-testid={`delete-reply-${reply.id}`}
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))
        )}
      </div>

      <style jsx="true">{`
        .comment-section .mention {
          color: #2563eb;
          font-weight: 500;
          background-color: #dbeafe;
          padding: 0 4px;
          border-radius: 4px;
        }
      `}</style>
    </div>
  );
}

export default CommentSection;
